import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ShoppingBag, Search, Eye, Calendar, Building2, ChevronLeft, ChevronRight, Download, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 20;

const statusConfig = {
  pending: { label: "Pendiente", className: "status-pending" },
  confirmed: { label: "Confirmado", className: "status-confirmed" },
  processing: { label: "Procesando", className: "status-processing" },
  shipped: { label: "Enviado", className: "status-shipped" },
  delivered: { label: "Entregado", className: "status-delivered" },
  cancelled: { label: "Cancelado", className: "status-cancelled" },
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = { 
        skip: page * PAGE_SIZE, 
        limit: PAGE_SIZE,
        search,
        status: statusFilter === "all" ? "" : statusFilter
      };
      const [ordersRes, countRes] = await Promise.all([
        axios.get(`${API}/admin/orders`, { params }),
        axios.get(`${API}/admin/orders/count`, { params: { search, status: statusFilter === "all" ? "" : statusFilter } })
      ]);
      setOrders(ordersRes.data);
      setTotalCount(countRes.data.count);
    } catch (error) {
      toast.error("Error al cargar los pedidos");
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setPage(0);
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status?status=${newStatus}`);
      setOrders(
        orders.map((o) =>
          o.order_id === orderId ? { ...o, status: newStatus } : o
        )
      );
      toast.success(`Estado actualizado a ${statusConfig[newStatus]?.label}`);
    } catch (error) {
      toast.error("Error al actualizar el estado");
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const downloadOrder = async (orderId, format) => {
    try {
      const res = await axios.get(`${API}/admin/orders/${orderId}/download/${format}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const ext = format === "pdf" ? "pdf" : "csv";
      a.download = `pedido_${orderId.substring(0, 8)}.${ext}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Error descargando pedido");
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-orders">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center">
            <ShoppingBag className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Pedidos</h1>
            <p className="text-slate-500">{totalCount} pedidos en total</p>
          </div>
        </div>

        {/* Filters */}
        <Card className="bg-white shadow-sm">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar por cliente, empresa o ID..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="pl-10"
                  data-testid="search-orders-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
                <SelectTrigger className="w-full sm:w-[180px]" data-testid="status-filter">
                  <SelectValue placeholder="Filtrar por estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  {Object.entries(statusConfig).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      {config.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Orders table */}
        <Card className="bg-white shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">Lista de Pedidos</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-12">
                <ShoppingBag className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">
                  {search || statusFilter
                    ? "No se encontraron pedidos"
                    : "No hay pedidos todavía"}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Pedido</TableHead>
                      <TableHead className="hidden md:table-cell">Cliente</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.map((order) => {
                      const status = statusConfig[order.status] || statusConfig.pending;
                      
                      return (
                        <TableRow
                          key={order.order_id}
                          className="table-row-hover"
                          data-testid={`order-row-${order.order_id}`}
                        >
                          <TableCell>
                            <div>
                              <p className="font-medium text-slate-900">
                                #{order.order_id.substring(0, 8)}
                              </p>
                              <p className="text-xs text-slate-500 flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {formatDate(order.created_at)}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            <div>
                              <p className="font-medium text-slate-900">
                                {order.user_name}
                              </p>
                              {order.user_company && (
                                <p className="text-sm text-slate-500 flex items-center gap-1">
                                  <Building2 className="w-3 h-3" />
                                  {order.user_company}
                                </p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-semibold text-slate-900">
                                {order.total.toFixed(2)}€
                              </p>
                              <p className="text-xs text-slate-500">
                                {order.items.length} productos
                              </p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Select
                              value={order.status}
                              onValueChange={(value) => handleStatusChange(order.order_id, value)}
                            >
                              <SelectTrigger
                                className="w-[130px] h-8"
                                data-testid={`status-select-${order.order_id}`}
                              >
                                <Badge className={status.className}>{status.label}</Badge>
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(statusConfig).map(([key, config]) => (
                                  <SelectItem key={key} value={key}>
                                    {config.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setSelectedOrder(order)}
                                title="Ver detalle"
                                data-testid={`view-order-${order.order_id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => downloadOrder(order.order_id, "csv")}
                                title="Descargar CSV"
                                data-testid={`download-csv-${order.order_id}`}
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => downloadOrder(order.order_id, "pdf")}
                                title="Descargar PDF"
                                data-testid={`download-pdf-${order.order_id}`}
                              >
                                <FileText className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-4 border-t">
                <p className="text-sm text-slate-500">
                  Mostrando {page * PAGE_SIZE + 1} - {Math.min((page + 1) * PAGE_SIZE, totalCount)} de {totalCount}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => p - 1)}
                    disabled={page === 0}
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Anterior
                  </Button>
                  <span className="text-sm text-slate-600">
                    Página {page + 1} de {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => p + 1)}
                    disabled={page >= totalPages - 1}
                  >
                    Siguiente
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Order detail dialog */}
        <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                Pedido #{selectedOrder?.order_id.substring(0, 8)}
              </DialogTitle>
              <DialogDescription>
                Revisión detallada del pedido y accesos a descarga CSV/PDF.
              </DialogDescription>
            </DialogHeader>
            
            {selectedOrder && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Cliente</span>
                    <p className="font-medium">{selectedOrder.user_name}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Empresa</span>
                    <p className="font-medium">{selectedOrder.user_company || "N/A"}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Código A3</span>
                    <p className="font-medium">{selectedOrder.user_erp_customer_code || "N/A"}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Sede</span>
                    <p className="font-medium">{selectedOrder.user_branch || "N/A"}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Email</span>
                    <p className="font-medium">{selectedOrder.user_email}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Fecha</span>
                    <p className="font-medium">{formatDate(selectedOrder.created_at)}</p>
                  </div>
                </div>
                
                <Separator />
                
                <div>
                  <h4 className="font-medium mb-3">Productos</h4>
                  <div className="space-y-2">
                    {selectedOrder.items.map((item, index) => (
                      <div
                        key={index}
                        className="p-3 bg-slate-50 rounded-lg space-y-1"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="font-medium text-slate-900">{item.product_name}</p>
                            <p className="text-xs text-slate-500 font-mono">{item.product_code || "-"}</p>
                          </div>
                          <span className="font-semibold text-right whitespace-nowrap">
                            {((item.unit_price_applied || item.price || 0) * item.quantity).toFixed(2)}€
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-slate-500">
                          <span>{item.quantity} {item.unit} × {(item.unit_price_applied || item.price || 0).toFixed(2)}€</span>
                          {item.discount_percent > 0 && (
                            <span className="text-green-600 font-medium">-{item.discount_percent}%</span>
                          )}
                          {item.price_source && (
                            <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                              item.price_source === "SPECIAL" ? "bg-amber-100 text-amber-700" :
                              item.price_source === "BRANCH" ? "bg-indigo-100 text-indigo-700" :
                              item.price_source === "NONE" ? "bg-red-100 text-red-700" :
                              "bg-slate-100 text-slate-600"
                            }`}>
                              {item.price_source === "SPECIAL" ? "Especial" :
                               item.price_source === "BRANCH" ? "Sede" :
                               item.price_source === "NONE" ? "Consultar" : "Base"}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {selectedOrder.notes && (
                  <>
                    <Separator />
                    <div>
                      <h4 className="font-medium mb-2">Notas del cliente</h4>
                      <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">
                        {selectedOrder.notes}
                      </p>
                    </div>
                  </>
                )}
                
                <Separator />
                
                <div className="flex items-center justify-between text-lg font-semibold">
                  <span>Total</span>
                  <span className="text-blue-600">
                    {selectedOrder.total.toFixed(2)}€
                  </span>
                </div>
                
                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    className="flex-1 gap-2"
                    onClick={() => downloadOrder(selectedOrder.order_id, "csv")}
                    data-testid="dialog-download-csv"
                  >
                    <Download className="w-4 h-4" /> CSV
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 gap-2"
                    onClick={() => downloadOrder(selectedOrder.order_id, "pdf")}
                    data-testid="dialog-download-pdf"
                  >
                    <FileText className="w-4 h-4" /> PDF
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
